// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings > Mail > Swipe actions. A live preview row plays the swipe
// you just picked (and can be swiped on a phone), each direction is chosen
// from icon tiles in its colour, then the short-swipe trays, the feel (swipe
// distance, haptics, undo window, delete confirmation) and the FYI card.

import kitStyles from '@app/features/app/components/dialogs/shared/SettingsKit.module.css';
import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import {fyiGroupName} from '@app/features/konvo_mail/components/MailFyiCard';
import {MailMarkIcon} from '@app/features/konvo_mail/components/MailFyiMark';
import {
	MAIL_SWIPE_ACTION_LABEL,
	MAIL_SWIPE_ACTION_TONE,
	mailSwipeActionIcon,
} from '@app/features/konvo_mail/components/MailSwipeActionMeta';
import {MAIL_SWIPE_TONE, MailSwipeRow, type MailSwipeSpec} from '@app/features/konvo_mail/components/MailSwipeRow';
import styles from '@app/features/konvo_mail/components/MailSwipeSettings.module.css';
import MailSwipePreferences, {
	MAIL_CLEANUP_THRESHOLDS,
	MAIL_SWIPE_ACTIONS,
	MAIL_TRAY_ACTIONS,
	MAIL_TRAY_MAX,
	MAIL_UNDO_CHOICES,
	type MailSwipeAction,
	type MailSwipeDirection,
	type MailSwipeDistance,
} from '@app/features/konvo_mail/state/MailSwipePreferences';
import {fyiRuleGroup} from '@app/features/konvo_mail/utils/MailFyi';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {
	ArrowClockwiseIcon,
	ArrowsLeftRightIcon,
	CheckIcon,
	HandTapIcon,
	SparkleIcon,
	TimerIcon,
	TrashIcon,
	VibrateIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {ActionButton, IconButton, Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

const FYI_ARCHIVE_READ_DESCRIPTOR = msg({
	message: 'Archived once read',
	comment: 'Settings > Mail > FYI mail: what happens to this group of FYI mail ("Always tidy" choice).',
});
const FYI_SKIP_DESCRIPTOR = msg({
	message: 'Skips the inbox',
	comment: 'Settings > Mail > FYI mail: this group of FYI mail goes straight to Archive ("Always tidy" choice).',
});
const FYI_NEVER_DESCRIPTOR = msg({
	message: 'Not FYI: stays in the inbox',
	comment: 'Settings > Mail > FYI mail: this group was marked "Not FYI".',
});
const FYI_RESET_RULE_DESCRIPTOR = msg({
	message: 'Back to the FYI card: {name}',
	comment:
		'Settings > Mail > FYI mail: accessible label of the button that clears a group choice. {name} is the group.',
});
const PREVIEW_RIGHT_DESCRIPTOR = msg({message: 'Preview: swipe right', comment: 'Swipe settings preview caption.'});
const PREVIEW_LEFT_DESCRIPTOR = msg({message: 'Preview: swipe left', comment: 'Swipe settings preview caption.'});
const PREVIEW_TRY_DESCRIPTOR = msg({
	message: 'Try it on your phone',
	comment: 'Swipe settings preview: the preview row can be swiped by hand on a phone.',
});

const Tiles = observer(function Tiles({
	actions,
	isOn,
	onPick,
	multi,
	flx,
}: {
	actions: ReadonlyArray<MailSwipeAction>;
	isOn: (action: MailSwipeAction) => boolean;
	onPick: (action: MailSwipeAction) => void;
	multi?: boolean;
	flx: string;
}) {
	const {i18n} = useLingui();
	return (
		<div className={styles.tiles} role="group" data-flx={flx}>
			{actions.map((action) => {
				const on = isOn(action);
				const tone = action === 'none' ? null : MAIL_SWIPE_TONE[MAIL_SWIPE_ACTION_TONE[action]];
				return (
					<button
						key={action}
						type="button"
						aria-pressed={on}
						className={clsx(styles.tile, on && styles.tileOn)}
						style={{'--tile-tone': tone ?? 'var(--text-secondary)'} as React.CSSProperties}
						onClick={() => onPick(action)}
						data-flx={`${flx}.${action}`}
					>
						<span className={styles.tileWell}>
							{mailSwipeActionIcon(action, 22)}
							{multi && on ? (
								<span className={styles.tileCheck}>
									<CheckIcon size={11} weight="bold" />
								</span>
							) : null}
						</span>
						<span className={styles.tileLabel}>{i18n._(MAIL_SWIPE_ACTION_LABEL[action])}</span>
					</button>
				);
			})}
		</div>
	);
});

const PreviewRow = observer(({direction}: {direction: MailSwipeDirection}) => {
	const {i18n} = useLingui();
	const spec = (action: MailSwipeAction): MailSwipeSpec | null =>
		action === 'none'
			? null
			: {
					key: action,
					label: i18n._(MAIL_SWIPE_ACTION_LABEL[action]),
					icon: mailSwipeActionIcon(action, 24),
					tone: MAIL_SWIPE_ACTION_TONE[action],
					removes: false,
					run: () => undefined,
				};
	return (
		<div className={styles.preview}>
			<div className={styles.previewCaption}>
				<HandTapIcon size={14} />
				<span>{i18n._(direction === 'right' ? PREVIEW_RIGHT_DESCRIPTOR : PREVIEW_LEFT_DESCRIPTOR)}</span>
				<span className={styles.previewTry}>{i18n._(PREVIEW_TRY_DESCRIPTOR)}</span>
			</div>
			<MailSwipeRow
				key={direction}
				className={styles.previewRow}
				leading={spec(MailSwipePreferences.right)}
				trailing={spec(MailSwipePreferences.left)}
				commitRatio={MailSwipePreferences.distanceRatio}
				haptics={MailSwipePreferences.haptics}
				demo={direction === 'right' ? 'leading' : 'trailing'}
				data-flx="konvo-mail.swipe-settings.preview"
			>
				<div className={styles.fakeRow}>
					<span className={styles.fakeAvatar}>RS</span>
					<span className={styles.fakeMain}>
						<span className={styles.fakeTop}>
							<strong>Robert Strydom</strong>
							<span>07:48</span>
						</span>
						<span className={styles.fakeSubject}>Re: Chalmar Server Access</span>
						<span className={styles.fakePreview}>Hi JP, Please try this IP: 192.168.2.198</span>
					</span>
				</div>
			</MailSwipeRow>
		</div>
	);
});

export const MailSwipeSettings = observer(() => {
	const {t, i18n} = useLingui();
	const [previewing, setPreviewing] = useState<MailSwipeDirection>('right');
	const prefs = MailSwipePreferences;
	const pick = (direction: MailSwipeDirection) => (action: MailSwipeAction) => {
		prefs.setAction(direction, action);
		setPreviewing(direction);
	};
	const distanceOptions: ReadonlyArray<{value: MailSwipeDistance; children: string}> = [
		{value: 'short', children: t`Short`},
		{value: 'medium', children: t`Medium`},
		{value: 'long', children: t`Long`},
	];
	const undoOptions = MAIL_UNDO_CHOICES.map((seconds) => ({
		value: String(seconds),
		children: seconds === 0 ? t`Off` : t`${seconds} s`,
	}));
	const thresholdOptions = MAIL_CLEANUP_THRESHOLDS.map((count) => ({value: String(count), children: String(count)}));
	return (
		<>
			<SettingsSection
				id="konvo-mail-swipe-actions"
				title={<Trans>Swipe actions</Trans>}
				description={<Trans>What a full swipe does on a mail in the list on your phone.</Trans>}
			>
				<PreviewRow direction={previewing} />
				<div className={styles.groupLabel}>
					<Trans>Swipe right</Trans>
				</div>
				<Tiles
					actions={MAIL_SWIPE_ACTIONS}
					isOn={(action) => prefs.right === action}
					onPick={pick('right')}
					flx="user.konvo-mail-settings-tab.swipe-right"
				/>
				<div className={styles.groupLabel}>
					<Trans>Swipe left</Trans>
				</div>
				<Tiles
					actions={MAIL_SWIPE_ACTIONS}
					isOn={(action) => prefs.left === action}
					onPick={pick('left')}
					flx="user.konvo-mail-settings-tab.swipe-left"
				/>
			</SettingsSection>
			<SettingsSection
				id="konvo-mail-swipe-tray"
				title={<Trans>Short swipe</Trans>}
				description={
					<Trans>A short swipe stops on a tray of quick actions. A long swipe still runs the full action.</Trans>
				}
			>
				<div className={kitStyles.rows}>
					<Switch
						label={t`Open a tray on a short swipe`}
						value={prefs.trayEnabled}
						onChange={prefs.setTrayEnabled}
						icon={<ArrowsLeftRightIcon size={20} />}
						data-flx="user.konvo-mail-settings-tab.tray-enabled"
					/>
				</div>
				{prefs.trayEnabled ? (
					<>
						<div className={styles.groupLabel}>
							<Trans>Tray after a short swipe left (up to {MAIL_TRAY_MAX})</Trans>
						</div>
						<Tiles
							multi
							actions={MAIL_TRAY_ACTIONS}
							isOn={(action) => prefs.leftTray.includes(action)}
							onPick={(action) => prefs.toggleTrayAction('left', action)}
							flx="user.konvo-mail-settings-tab.tray-left"
						/>
						<div className={styles.groupLabel}>
							<Trans>Tray after a short swipe right (up to {MAIL_TRAY_MAX})</Trans>
						</div>
						<Tiles
							multi
							actions={MAIL_TRAY_ACTIONS}
							isOn={(action) => prefs.rightTray.includes(action)}
							onPick={(action) => prefs.toggleTrayAction('right', action)}
							flx="user.konvo-mail-settings-tab.tray-right"
						/>
					</>
				) : null}
			</SettingsSection>
			<SettingsSection id="konvo-mail-swipe-feel" title={<Trans>Swipe feel</Trans>}>
				<div className={kitStyles.rows}>
					<SettingsRow
						icon={<ArrowsLeftRightIcon size={20} />}
						title={<Trans>Swipe distance</Trans>}
						description={<Trans>How far a swipe must go before letting go runs the action.</Trans>}
						control={() => (
							<Segmented
								compact
								value={prefs.distance}
								options={distanceOptions}
								onChange={prefs.setDistance}
								data-flx="user.konvo-mail-settings-tab.distance"
							/>
						)}
					/>
					<Switch
						label={t`Haptic tick when armed`}
						description={t`A light tap as the swipe passes the line.`}
						value={prefs.haptics}
						onChange={prefs.setHaptics}
						icon={<VibrateIcon size={20} />}
						data-flx="user.konvo-mail-settings-tab.haptics"
					/>
					<SettingsRow
						icon={<TimerIcon size={20} />}
						title={<Trans>Undo window</Trans>}
						description={<Trans>After archive, delete or move, a notice at the top lets you undo.</Trans>}
						control={() => (
							<Segmented
								compact
								value={String(prefs.undoSeconds)}
								options={undoOptions}
								onChange={(value) => prefs.setUndoSeconds(Number(value))}
								data-flx="user.konvo-mail-settings-tab.undo"
							/>
						)}
					/>
					<Switch
						label={t`Confirm before a swipe deletes`}
						value={prefs.confirmDelete}
						onChange={prefs.setConfirmDelete}
						icon={<TrashIcon size={20} />}
						data-flx="user.konvo-mail-settings-tab.confirm-delete"
					/>
				</div>
			</SettingsSection>
			<SettingsSection
				id="konvo-mail-tidy-up"
				title={<Trans>FYI mail</Trans>}
				description={
					<Trans>
						Mail you only need to glance at, such as GitHub notifications, deploy runs and digests, gathers on one card
						at the top of your inbox.
					</Trans>
				}
			>
				<div className={kitStyles.rows}>
					<Switch
						label={t`Show the FYI card`}
						value={prefs.senderCleanup}
						onChange={prefs.setSenderCleanup}
						icon={<SparkleIcon size={20} />}
						data-flx="user.konvo-mail-settings-tab.cleanup"
					/>
					{prefs.senderCleanup ? (
						<SettingsRow
							icon={<SparkleIcon size={20} />}
							title={<Trans>FYI mails before the card shows</Trans>}
							description={<Trans>The card waits until this many are in your inbox.</Trans>}
							control={() => (
								<Segmented
									compact
									value={String(prefs.cleanupThreshold)}
									options={thresholdOptions}
									onChange={(value) => prefs.setCleanupThreshold(Number(value))}
									data-flx="user.konvo-mail-settings-tab.cleanup-threshold"
								/>
							)}
						/>
					) : null}
					{Object.entries(KonvoCalendarPrefs.mailFyiRules).map(([key, rule]) => {
						const group = fyiRuleGroup(key);
						const name = fyiGroupName(i18n, group);
						return (
							<SettingsRow
								key={key}
								icon={<MailMarkIcon kind={group.kind} source={group.source} size={20} />}
								title={name}
								description={i18n._(
									rule === 'archive_read'
										? FYI_ARCHIVE_READ_DESCRIPTOR
										: rule === 'skip'
											? FYI_SKIP_DESCRIPTOR
											: FYI_NEVER_DESCRIPTOR,
								)}
								control={() => (
									<IconButton
										label={i18n._(FYI_RESET_RULE_DESCRIPTOR, {name})}
										icon={<XIcon size={18} />}
										onClick={() => void KonvoCalendarPrefs.setMailFyiRule(key, null)}
										data-flx="user.konvo-mail-settings-tab.fyi-rule.reset"
									/>
								)}
								data-flx="user.konvo-mail-settings-tab.fyi-rule"
							/>
						);
					})}
				</div>
				<div className={styles.resetRow}>
					<ActionButton
						small
						tone="secondary"
						icon={<ArrowClockwiseIcon size={16} />}
						disabled={!prefs.hintShown}
						onClick={prefs.resetHint}
						data-flx="user.konvo-mail-settings-tab.reset-hint"
					>
						<Trans>Show the swipe hint again</Trans>
					</ActionButton>
				</div>
			</SettingsSection>
		</>
	);
});
